import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Key, Lock, Mail, AlertCircle, Loader2, Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'sonner';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAdmin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    console.log('[Admin Login Page] Attempting login for:', email);
    
    try {
      await login(email, password);
      toast.success('Admin login successful');
      navigate('/admin');
    } catch (err) {
      // Show specific error from backend
      const message = err.message || err.response?.data?.detail || 'Admin login failed. Please check your credentials.';
      console.error('[Admin Login Page] Error:', message);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Admin Login Only Badge */}
        <div className="flex justify-center mb-6">
          <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 px-4 py-1.5 text-sm">
            <Shield className="w-4 h-4 mr-2" />
            Admin Login Only
          </Badge>
        </div>

        <div className="text-center mb-8">
          <img 
            src="/staypairhomes-logo.png" 
            alt="Stay Pair Homes" 
            className="h-16 w-auto mx-auto mb-4 object-contain"
          />
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Admin Access
          </h1>
          <p className="text-slate-400">
            Stay Pair Homes Platform Administration
          </p>
        </div>

        <div className="bg-slate-800 rounded-2xl p-8 border border-slate-700">
          {error && (
            <div className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-xl flex items-center gap-3 text-red-400">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@staypairhomes.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="admin-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  required
                  data-testid="admin-password"
                />
              </div>
              
              {/* Forgot Password Link */}
              <div className="text-right">
                <Link 
                  to="/admin/forgot-password"
                  className="text-sm text-trust-blue hover:text-blue-400 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-trust-blue hover:bg-trust-blue-hover text-white h-12"
              disabled={loading}
              data-testid="admin-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  <Key className="w-4 h-4 mr-2" />
                  Access Admin Panel
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-slate-500 text-sm">
            Protected area. Unauthorized access is prohibited.
          </p>

          {/* User Login Link */}
          <div className="mt-4 pt-4 border-t border-slate-700">
            <Link 
              to="/login" 
              className="flex items-center justify-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
              data-testid="user-login-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to User Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
